import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchAllTreatments } from '../../lib/billing.js'
import { fmtDateText } from '../../kit/format.js'

/**
 * ‹ ครั้งก่อน · 3 / 12 · ครั้งถัดไป › — step through a patient's visits from the
 * chart detail page without going back to History. ← / → do the same.
 *
 * Order is the History list's: newest first, by treatment date, rows without a
 * date left out (PatientChartHistory groupByMonth). ← goes to the OLDER visit
 * (ครั้งก่อน), → to the NEWER one (ครั้งถัดไป), like a timeline.
 *
 * `blocked` = the page has unsaved edits. Moving would remount the editors on
 * another chart and drop the draft, so both the buttons and the keys refuse
 * until the save bar is saved or cancelled.
 *
 * The list is cached per patient for a minute, so flipping through visits costs
 * one fetch, not one per step.
 */

const TTL_MS = 60 * 1000
const cache = new Map() // patientId → { at, items }

function parseDate(v) {
  if (!v) return null
  if (typeof v === 'number') return new Date(v)
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s.slice(0, 10) + 'T00:00:00')
  const d = new Date(s)
  return isNaN(d) ? null : d
}

// On-screen dates are ค.ศ. (kit decision #1) — this used to print a 2-digit พ.ศ.
const shortDate = (v) => (parseDate(v) ? fmtDateText(v) : '')

const tidOf = (t) => t?.id ?? t?.treatment_id

// The keys must never steal an arrow from someone typing or from an open dialog.
function keyIsFree(e) {
  if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.isComposing) return false
  const el = e.target
  if (el?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return false
  if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return false
  return true
}

export default function ChartVisitNav({ patientId, treatmentId, blocked = false }) {
  const navigate = useNavigate()
  const [items, setItems] = useState(() => {
    const hit = cache.get(String(patientId))
    return hit && Date.now() - hit.at < TTL_MS ? hit.items : null
  })

  useEffect(() => {
    const hit = cache.get(String(patientId))
    if (hit && Date.now() - hit.at < TTL_MS) { setItems(hit.items); return }
    let alive = true
    fetchAllTreatments(patientId)
      .then((list) => {
        const dated = (list || []).filter((t) => parseDate(t?.date))
        // Stable sort, newest first — same order as the History list.
        const ordered = dated
          .map((t, i) => ({ t, i, ms: parseDate(t.date).getTime() }))
          .sort((a, b) => (b.ms - a.ms) || (a.i - b.i))
          .map((x) => x.t)
        cache.set(String(patientId), { at: Date.now(), items: ordered })
        if (alive) setItems(ordered)
      })
      .catch(() => { if (alive) setItems([]) })
    return () => { alive = false }
  }, [patientId])

  const idx = useMemo(
    () => (items ? items.findIndex((t) => String(tidOf(t)) === String(treatmentId)) : -1),
    [items, treatmentId],
  )
  const older = idx >= 0 ? items[idx + 1] : null
  const newer = idx > 0 ? items[idx - 1] : null

  const go = (t) => {
    if (!t || blocked) return
    navigate(`/patient/${patientId}/chart/history/${tidOf(t)}`, { state: { treatment: t } })
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (!keyIsFree(e)) return
      const target = e.key === 'ArrowLeft' ? older : newer
      if (!target || blocked) return
      e.preventDefault()
      go(target)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [older, newer, blocked])

  // Nothing to step through: still loading, this visit isn't in the list, or it's the only one.
  if (!items || idx < 0 || items.length < 2) return null

  const btn = 'inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600 disabled:cursor-not-allowed transition-colors'

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btn}
          disabled={!older || blocked}
          onClick={() => go(older)}
          title={older ? `ครั้งก่อน · ${shortDate(older.date)} (←)` : 'ไม่มีครั้งก่อนหน้า'}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          <span>ครั้งก่อน</span>
          {older && <span className="hidden sm:inline text-slate-400">{shortDate(older.date)}</span>}
        </button>
        <span className="text-[12.5px] tabular-nums text-slate-400" title="ลำดับจากล่าสุด">
          {idx + 1} / {items.length}
        </span>
        <button
          type="button"
          className={btn}
          disabled={!newer || blocked}
          onClick={() => go(newer)}
          title={newer ? `ครั้งถัดไป · ${shortDate(newer.date)} (→)` : 'นี่คือครั้งล่าสุด'}
        >
          {newer && <span className="hidden sm:inline text-slate-400">{shortDate(newer.date)}</span>}
          <span>ครั้งถัดไป</span>
          <ChevronRight size={14} strokeWidth={2} />
        </button>
      </div>
      {blocked && (
        <span className="text-[12px] text-amber-700">บันทึกหรือยกเลิกการแก้ไขก่อนเปลี่ยนครั้ง</span>
      )}
    </div>
  )
}
